/**
 * PublishingCardSE — right-rail publishing summary for a Series.
 *
 *   • Active pill in the head
 *   • Parent category name (resolved from the categories slice)
 *   • Sessions count (drawn from the sortable list)
 *   • Mark-as-new badge when toggled
 *
 * Plus the canonical Activate / Deactivate link in the footer.
 */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

export default function PublishingCardSE({
  state,
  sessionCount,
  onToggleActive,
}) {
  const isActive = !!state.series_is_active;
  const isNew = !!state.series_is_new;

  const all = useSelector((s) => s.categories?.all_categories);
  const parentCategoryTitle = useMemo(() => {
    if (!all || !state.category) return null;
    const list = Array.isArray(all) ? all : Object.values(all);
    const hit = list.find((c) => Number(c.category_id) === Number(state.category));
    return hit?.category_title || null;
  }, [all, state.category]);

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' : '#9CA1A81F',
                color: isActive ? '#5C7E72' : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Type
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: '#1AA6B714',
                  color: '#1A8A98',
                }}
              >
                Series
              </span>
              {isNew && (
                <span
                  className="font-condensed font-bold uppercase"
                  style={{
                    marginLeft: 6,
                    fontSize: 10,
                    letterSpacing: '0.1em',
                    padding: '3px 9px',
                    borderRadius: 999,
                    background: 'linear-gradient(135deg, #FFC107, #F5A623)',
                    color: '#2C3E50',
                  }}
                >
                  New
                </span>
              )}
            </dd>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Parent category
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {parentCategoryTitle || '—'}
            </dd>
          </div>

          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Sessions
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {sessionCount ?? 0}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Days
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {sessionCount > 0 ? `~${sessionCount}` : '—'}
            </dd>
          </div>
        </dl>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' : '#5C7E72',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            {isActive ? 'Deactivate series' : 'Activate series'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
